import {
  useCanvasFileRecords,
  useHydrateFileRecordEntity,
} from "@/api/fileRecords";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useHtmlPresignRewrite } from "@/hooks/useHtmlPresignRewrite";
import {
  buildCanvasNodeKind,
  getActionIcon,
  getActionLabel,
  getObjectActionDefinition,
  resolvedActionsToContextMenuActions,
  useCanvasEntityObjectActions,
} from "@/object-actions";
import type {
  ObjectActionHandlers,
  ObjectActionId,
} from "@/object-actions/types";
import { useCanvasEntityNavigationHandlers } from "@/pages/canvas/hooks/useCanvasEntityNavigationHandlers";
import { useCanvasFileRecordMenuHandlerDeps } from "@/pages/canvas/hooks/useCanvasFileRecordMenuHandlerDeps";
import {
  useHtmlBlockNode,
  type HtmlBlockNodeState,
} from "@/pages/canvas/hooks/useHtmlBlockNode";
import { useHydrateCanvasLinkEntity } from "@/pages/canvas/hooks/useHydrateCanvasLinkEntity";
import { useOpenCanvasLink } from "@/pages/canvas/hooks/useOpenCanvasLink";
import type { EntityNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { isCanvasLinkEntity } from "@/pages/canvas/utils/canvasLinkEntity";
import { openCanvasEntityTabUrl } from "@/pages/canvas/utils/resolveCanvasEntityOpenTabUrl";
import { resolveFileRecordFromList } from "@/pages/canvas/utils/resolveFileRecordFromList";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import {
  BaseEntityCardFloatingToolbar,
  BaseEntityCardQuickChat,
  blockDisplayFromNodeData,
  CanvasEntityCard,
  CanvasObjectChrome,
  FileRecordCardSkeleton,
  FileRecordCardUnavailable,
  getEntityData,
  type BlockDisplay,
  type ContextMenuAction,
  type Entity,
  type FileRecordSkeletonKind,
} from "@cohesive/ui-components";
import { NodeResizer, useUpdateNodeInternals } from "@xyflow/react";
import { AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  ExternalLink,
  FileText,
  Frame,
  MousePointerClick,
  Play,
} from "lucide-react";
import {
  createElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import {
  useCanvasLocalFilePreview,
  useCanvasLocalFilePreviewActions,
} from "../context/CanvasLocalFilePreviewContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  useNodeCanEdit,
  useNodeLocked,
  useShowUnlockToolbar,
} from "../hooks/useNodeCanEdit";
import { useOpenNodeContextMenu } from "../hooks/useOpenNodeContextMenu";
import { useQuantizedZoom } from "../hooks/useQuantizedZoom";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { useVideoHandoffReady } from "../hooks/useVideoHandoffReady";
import {
  fileRecordIsProcessed,
  fileRecordIsUnavailable,
  mergeFileRecordWithLocalPreview,
  shouldRevokeLocalFilePreview,
  shouldShowFileRecordSkeleton,
} from "../utils/fileRecordDisplay";
import { resolveGenericEntityPaneTarget } from "../utils/genericEntityPane";
import {
  DEFAULT_FILE_NODE_HEIGHT,
  DOC_PORTRAIT_NODE_HEIGHT,
  DOC_PORTRAIT_NODE_WIDTH,
  getEntityMediaDisplayBox,
  getFileRecordMediaDisplayBoxFromMetadata,
  isPageFileRecord,
} from "../utils/imageDimensions";
import { HtmlBlockLightbox } from "./HtmlBlockLightbox";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";
import { CanvasUnlockToolbar } from "./shared/CanvasUnlockToolbar";

// Temporary: file entity node toolbars only surface these utilities (in this
// order). Chat + delete render separately; the rest live under "More". Share
// is manifest-gated, so the order needs no per-node variant — a block shows
// the same buttons as a document, with Expand in its menus.
const FILE_ENTITY_TOOLBAR_UTILITY_ORDER: ObjectActionId[] = [
  "share",
  "download",
  "open",
];

// Default card width — used when no width is stored in Loro yet.
const DEFAULT_WIDTH = 320;

// Matches fitImageNodeSize(..., minShortEdge); generic file nodes stay at 100.
// One resize floor for every entity card (ruled in M1 review): today's
// canvas file records — media, documents, HTML, tiles — are all scalable
// previews, so they shrink alike. 128 = 8 lattice cells, still reads as
// content. Per-object milestones re-raise specific types when dense DS
// card bodies land (recorded in the geometry matrix when they do).
const ENTITY_RESIZER_MIN = 128;

// Toolbar entry point for block ↔ document conversion, hidden for now (the
// action caused UX confusion). The conversion machinery itself stays live —
// CanvasEdit's per-node `display`, `canvas.removeNodeDataKey`, the in-place
// marker flip — so flipping this back on restores the buttons as-is.
const DISPLAY_MODE_CONVERSION_TOOLBAR = false;

// Several classes of legacy entities are intentionally not rendered on the
// canvas surface anymore. All wrappers return `null` from the React Flow node
// component, which fully hides the node — selection rings, handles, the lot —
// without touching Loro state. Backend cleanup (deleting the rows, dropping
// the supporting tables) happens in a later pass.
//
//   1. `type: "artifact"` — content migrated into file_records.
//
//   2. `base: "file"` AND (platform === "cohesive" OR type === "canvas_file")
//      — the legacy rag_files-backed canvas uploads. External `base: "file"`
//      entities (Google Drive, Dropbox, Slack file shares, etc.) keep
//      rendering through the normal external-file path.
//
//   3. `base: "image"` | `base: "video"` — legacy media nodes.
//      These still render in chat via the legacyFileEntityDataToFileRecord
//      adapter; they are hidden on canvas only.
//
// The guard runs *before* any hooks in the inner component, so we don't pay
// for stale fetches / effects on hidden nodes.
function isLegacyHiddenEntityProps(props: EntityNodeProps): boolean {
  const e = props.data?.entity as
    | { base?: string; type?: string; platform?: string }
    | undefined;
  if (!e) return false;
  if (e.type === "artifact") return true;
  if (e.base === "image" || e.base === "video") return true;
  if (
    e.base === "file" &&
    (e.platform === "cohesive" || e.type === "canvas_file")
  ) {
    return true;
  }
  return false;
}

export const EntityNode = memo(function EntityNode(props: EntityNodeProps) {
  if (isLegacyHiddenEntityProps(props)) return null;
  // Routing on the resolved display model (rather than mounting the block
  // hooks unconditionally) keeps image/PDF/doc nodes free of the presign map
  // and the other per-block machinery. The node's display copy is read
  // through blockDisplayFromNodeData — the one place that understands both
  // the current `data.display` and the legacy `data.presentation` marker.
  // The model CAN flip in place (display-mode conversion): the component
  // identity changes, so React remounts the subtree cleanly — the focus
  // latch releases (and flushes) on unmount, and the other card renders
  // fresh.
  const display = blockDisplayFromNodeData(props.data);
  if (display?.mode === "block") {
    return <HtmlBlockEntityNode {...props} display={display} />;
  }
  return <EntityNodeImpl {...props} htmlBlock={null} />;
});

/** Mounts the HTML-block hooks, then renders the shared node body. */
function HtmlBlockEntityNode({
  display,
  ...props
}: EntityNodeProps & { display: BlockDisplay }) {
  const { expandedNodeId, collapseNode } = useCanvasApi();
  const canEdit = useNodeCanEdit(props.id);
  const fileRecordId = (props.data?.entity?.data as { id?: string } | undefined)
    ?.id;
  const htmlBlock = useHtmlBlockNode({
    nodeId: props.id,
    display,
    fileRecordId,
    loading: props.data?.loading === true,
    selected: !!props.selected,
    dragging: !!props.dragging,
    canEdit,
    // The lightbox is canvas state (one node open at a time) so that every
    // route into it — toolbar, node menu, Enter, the rail — is the same
    // canvas.expandNode call; the hook owns what happens while it is open.
    expanded: expandedNodeId === props.id,
    collapse: collapseNode,
  });
  return <EntityNodeImpl {...props} htmlBlock={htmlBlock} />;
}

function EntityNodeImpl({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
  htmlBlock,
}: EntityNodeProps & { htmlBlock: HtmlBlockNodeState | null }) {
  const canvas = useCanvasApi();
  const canEdit = useNodeCanEdit(id);
  const locked = useNodeLocked(id);
  const showUnlockToolbar = useShowUnlockToolbar(id, selected, dragging);
  // Presign rewrite for HTML cards WITHOUT a block presentation (legacy and
  // agent-created records — the backend never sets `presentation`). The card
  // iframe is an opaque-origin sandbox that sends no credentials, so embedded
  // public/{id} asset URLs must be swapped for presigned ones before display;
  // block nodes bring their own rewrite (shared with their edit session).
  // Idle cost is refs only — the rows behind the references are asked for
  // solely when an HTML card actually renders content that references files.
  const presign = useHtmlPresignRewrite({
    fileRecordId: (data?.entity?.data as { id?: string } | undefined)?.id,
  });
  // Public share view: pace HTML-card iframe mounts through the shared
  // admission scheduler instead of bursting them all on load (the burst
  // kills the WebKit renderer on iPhones for block-heavy canvases). The
  // editor keeps the eager default — byte-identical behavior.
  const isPublicCanvasView = useIsPublicCanvasView();
  const { dispatchProcess, highlightedNodeIds } = useCanvasProcess();
  const { isResizingRef, cursorTool } = useCanvasUi();
  const isHighlighted = highlightedNodeIds.has(id);
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const appNavigate = useAppNavigate();
  const openFileRecord = useOpenFileRecord();
  const openCanvasLink = useOpenCanvasLink();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();

  // O(1) — subscribes to only the selection count scalar, not full node array.
  const selectedNodeCount = useCanvasSelectionCount();
  const zoom = useQuantizedZoom();

  // Click-through gate — canvas-side guards only; the card owns the
  // gesture-shape ones (first click, modifiers, movement). Multi-select must
  // never activate content, and mid-gesture tools keep the pointer.
  const blockClickThrough =
    !!htmlBlock &&
    htmlBlock.focusMode === null &&
    data?.loading !== true &&
    !dragging &&
    cursorTool !== "hand" &&
    selectedNodeCount <= 1;

  // The block's state, in the chrome foot. While the user is inside a block
  // it reports the mode; at rest it advertises what the gestures do. Both are
  // the same kind of fact — what this object is doing or will do — so they
  // share one line rather than competing for space.
  const blockStatus =
    htmlBlock == null || data?.loading
      ? undefined
      : htmlBlock.focusMode === "edit"
        ? htmlBlock.saveState.isSaving
          ? "Editing — saving…"
          : htmlBlock.saveState.pendingCount > 0
            ? "Editing — unsaved changes"
            : "Editing — Esc to finish"
        : htmlBlock.focusMode === "interact"
          ? "Interacting — Esc to exit"
          : htmlBlock.enterHintLabel;
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);
  // True while this block is the node open full-size in the lightbox
  // (canvas.expandedNodeId — set by double-click, Expand on the toolbar or
  // node menu, Enter, or the rail). The wrapper advertises it
  // (data-block-expanded) and mounts the overlay below.
  const isExpanded = htmlBlock?.lightbox.isOpen ?? false;

  // Clear chat latch on multi-select or deselect — node stays selected during
  // multi-select, so !selected alone would leave showChatInput true and remount
  // chat UI when returning to single-select.
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
    }
  }, [isMultiSelectToolbarOpen, selected]);

  const isFileRecordEntity = !!(
    data?.entity &&
    "base" in data.entity &&
    data.entity.base === "file_record"
  );

  const localPreview = useCanvasLocalFilePreview(id);
  const { revoke: revokeLocalPreview } = useCanvasLocalFilePreviewActions();

  const {
    entity: hydratedFileRecordEntity,
    isGone: fileRecordHydrationGone,
    isSettled: fileRecordHydrationSettled,
  } = useHydrateFileRecordEntity(
    isFileRecordEntity ? (data?.entity as any) : null,
  );

  // A canvas link stores id + name; the live record supplies the fresh name,
  // hero image and workspace location the card renders.
  const { entity: hydratedCanvasLinkEntity, hydration: canvasLinkHydration } =
    useHydrateCanvasLinkEntity(data?.entity);

  // Hydrated file records carry full backend metadata (content_type,
  // preview_url, etc.) that the toolbar / preview render against. Other
  // entity types pass through the raw Loro entity unchanged.
  const effectiveEntity = useMemo(() => {
    if (isFileRecordEntity && hydratedFileRecordEntity) {
      return hydratedFileRecordEntity;
    }
    if (hydratedCanvasLinkEntity) {
      return hydratedCanvasLinkEntity;
    }
    return data?.entity;
  }, [
    isFileRecordEntity,
    data?.entity,
    hydratedFileRecordEntity,
    hydratedCanvasLinkEntity,
  ]);

  const layoutEntity = effectiveEntity ?? data?.entity;

  const isVideoLocalPreview =
    !!localPreview && localPreview.contentType.startsWith("video/");

  const serverFileRecordData = isFileRecordEntity
    ? (effectiveEntity?.data as
        | {
            status?: string;
            url?: string;
            preview_urls?: Record<string, string> | null;
          }
        | undefined)
    : undefined;

  const serverVideoUrl =
    isVideoLocalPreview &&
    fileRecordIsProcessed(serverFileRecordData?.status) &&
    serverFileRecordData?.url
      ? serverFileRecordData.url
      : undefined;

  const videoHandoffReady = useVideoHandoffReady(
    serverVideoUrl,
    !!serverVideoUrl,
  );

  const mergeOptions = useMemo(
    () => ({ videoHandoffReady }),
    [videoHandoffReady],
  );

  const fileRecordCardEntity = useMemo(() => {
    const base = layoutEntity;
    if (!isFileRecordEntity || !base || !("data" in base) || !localPreview) {
      return base;
    }
    const mergedData = mergeFileRecordWithLocalPreview(
      base.data as {
        status?: string;
        url?: string;
        content_type?: string;
        preview_urls?: Record<string, string> | null;
      },
      localPreview,
      mergeOptions,
    );
    if (mergedData === base.data) return base;
    return { ...base, data: mergedData } as Entity;
  }, [isFileRecordEntity, layoutEntity, localPreview, mergeOptions]);

  // The lightbox renders from the same entity the node card renders, so it
  // can never be thinner than the card (the export record may be the wire
  // stub, whose missing presigned url would leave the overlay empty).
  const lightboxRecord = htmlBlock
    ? (getEntityData(
        (fileRecordCardEntity ?? layoutEntity ?? data?.entity) as Entity,
      ) as FileRecord | undefined)
    : undefined;

  // Revoke blob for images once processed; videos revoke in onVideoDisplayReady
  // after the server stream paints (see useVideoHandoffReady + thumbnail hold).
  useEffect(() => {
    if (!localPreview) return;
    if (shouldRevokeLocalFilePreview(serverFileRecordData, localPreview)) {
      revokeLocalPreview(id);
    }
  }, [id, localPreview, serverFileRecordData, revokeLocalPreview]);

  const handleVideoDisplayReady = useCallback(() => {
    if (!localPreview?.contentType.startsWith("video/")) return;
    if (!fileRecordIsProcessed(serverFileRecordData?.status)) return;
    if (!videoHandoffReady) return;
    revokeLocalPreview(id);
  }, [
    id,
    localPreview,
    serverFileRecordData?.status,
    videoHandoffReady,
    revokeLocalPreview,
  ]);

  // Determine resize behaviour from entity type
  const entity = layoutEntity;
  // A canvas link is a landscape hero tile: resizable, aspect kept.
  const isCanvasLink = !!entity && isCanvasLinkEntity(entity);
  // HTML file records get free-form resize (no aspect lock).
  const isHtmlFileRecord =
    !!entity &&
    "base" in entity &&
    entity.base === "file_record" &&
    ((entity.data as { content_type?: string })?.content_type ?? "")
      .split(";")[0]
      .trim() === "text/html";
  // Progressive-mount placeholder for HTML cards on the public share view:
  // the published artifact carries a server-rendered 640px JPEG per file
  // (enrichPublicCanvasNodes maps files[id].previews → preview_urls, and
  // nulls out stale in-app values so this can never be an expired presigned
  // URL), shown until the scheduler admits the live iframe. Editor passes
  // nothing.
  const htmlPreviewImageUrl =
    isPublicCanvasView && isHtmlFileRecord
      ? serverFileRecordData?.preview_urls?.["640"]
      : undefined;
  // Generic entities with a cover image render as an embedded thumbnail tile
  // (see CanvasEntityCard); treat them like file tiles for sizing + resize.
  const isGenericTile = !!(
    entity &&
    "base" in entity &&
    entity.base === "generic" &&
    (entity.data as { image_url?: string })?.image_url
  );
  const isSquareResizable = !!(
    entity &&
    "base" in entity &&
    (entity.base === "file" ||
      (entity.base === "file_record" && !isHtmlFileRecord) ||
      isGenericTile)
  );
  const fittedMediaBox = entity
    ? (getEntityMediaDisplayBox(entity) ??
      getFileRecordMediaDisplayBoxFromMetadata(entity))
    : null;
  // Every non-media file record (markdown, html documents, pdf, office
  // documents, …) presents as a portrait page; an html BLOCK is the one
  // html record that is not a page, and a web-page capture is a screenshot
  // at its own aspect (fittedMediaBox above).
  const isPageRecord =
    !htmlBlock &&
    !!entity &&
    "base" in entity &&
    entity.base === "file_record" &&
    isPageFileRecord(
      entity.data as { content_type?: string; origin_type?: string },
    );
  // Preserve square locking for all square cards (image/video/file).
  // Fitted media boxes (e.g. image/* attachments) also keep their computed
  // aspect. HTML file records are excluded — they resize freely — and so is
  // every page: the page look is a default, not a lock.
  const keepResizeAspect =
    (isSquareResizable && !isPageRecord) ||
    fittedMediaBox != null ||
    isCanvasLink;
  const resizerMinW = ENTITY_RESIZER_MIN;
  const resizerMinH = ENTITY_RESIZER_MIN;
  const isResizable = isSquareResizable || isHtmlFileRecord || isCanvasLink;

  // React Flow passes width=0 for nodes that haven't been measured yet.
  // Use || (not ??) so that 0 also falls back to the default.
  const nodeWidth =
    width ||
    fittedMediaBox?.width ||
    (isPageRecord ? DOC_PORTRAIT_NODE_WIDTH : DEFAULT_WIDTH);
  // Explicit height: fitted media and file-ish nodes need a stable host box.
  // Agent-placed file_records can arrive with only id/title and no Loro
  // dimensions, so pages fall back to the portrait page and media without
  // dimensions to the landscape tile instead of letting the embedded thumb
  // collapse.
  const isFileRecord =
    !!entity && "base" in entity && entity.base === "file_record";
  const isLegacyFileBase =
    !!entity && "base" in entity && entity.base === "file";
  const nodeHeight =
    height && height > 0
      ? height
      : fittedMediaBox != null
        ? fittedMediaBox.height
        : isCanvasLink
          ? DEFAULT_FILE_NODE_HEIGHT
          : isHtmlFileRecord
            ? // Blocks render 1:1 with no `aspect-square` fallback, so a node
              // that arrives without a stored height would collapse to nothing;
              // html documents take the portrait page like markdown.
              htmlBlock
              ? DEFAULT_FILE_NODE_HEIGHT
              : DOC_PORTRAIT_NODE_HEIGHT
            : isPageRecord
              ? DOC_PORTRAIT_NODE_HEIGHT
              : isFileRecord || isLegacyFileBase || isGenericTile
                ? DEFAULT_FILE_NODE_HEIGHT
                : undefined;

  // Process actions — AI/automation only (rendered purple in toolbar)
  const entityActions = useMemo(() => {
    if (!layoutEntity) return [];

    const entity = layoutEntity;
    const actions: ContextMenuAction[] = [];

    // Action item — execute
    if ("base" in entity && entity.base === "action_item") {
      const entityData = getEntityData(entity);
      const canExecute = entityData?.status === "ready";
      if (canExecute) {
        actions.push({
          key: "execute-action-item",
          label: "Execute action item",
          icon: <Play className="h-5 w-5" />,
          onClick: () => {
            dispatchProcess({
              nodeId: id,
              entity,
              action: "execute_action_item",
            });
          },
        });
      }
    }

    // HTML blocks — the toolbar button puts the user INSIDE the block (edit
    // when islands + canEdit, else interact) and lights up while any entry
    // is active, however it was entered. (Enter is Expand, not entry.)
    // Clicking while inside leaves, like Esc. Standard chrome, not accent:
    // the icon reads black/white per theme until the toggle is active.
    if (htmlBlock && canEdit && !data?.loading) {
      const engaged = htmlBlock.focusMode !== null;
      actions.push({
        key: "toggle-block-interactive",
        label: engaged ? "Stop interacting" : "Interact",
        icon: <MousePointerClick className="h-5 w-5" />,
        active: engaged,
        accent: false,
        onClick: () => {
          if (engaged) {
            htmlBlock.exit();
            return;
          }
          htmlBlock.requestEnter();
        },
      });
    }

    // Display-mode conversion for HTML file records — the user-side repair
    // for a wrong block-vs-document choice at create time (mirrors
    // CanvasEdit's per-node `display` conversion). Currently hidden — see
    // DISPLAY_MODE_CONVERSION_TOOLBAR.
    if (
      DISPLAY_MODE_CONVERSION_TOOLBAR &&
      isHtmlFileRecord &&
      canEdit &&
      !data?.loading
    ) {
      if (htmlBlock) {
        actions.push({
          key: "show-as-document",
          label: "Show as document",
          icon: <FileText className="h-5 w-5" />,
          accent: false,
          onClick: () => {
            // Exit first so the focus latch's falling edge flushes pending
            // island saves; the marker removal then unmounts the block
            // machinery, whose unmount path cannot flush.
            htmlBlock.exit();
            setTimeout(() => {
              canvas.removeNodeDataKey(id, "display");
              canvas.removeNodeDataKey(id, "presentation");
            }, 0);
          },
        });
      } else {
        actions.push({
          key: "show-as-block",
          label: "Show as block",
          icon: <Frame className="h-5 w-5" />,
          accent: false,
          onClick: () => {
            canvas.updateNodeData(id, {
              display: { mode: "block" },
              presentation: {},
            });
          },
        });
      }
    }

    return actions;
  }, [
    layoutEntity,
    id,
    dispatchProcess,
    htmlBlock,
    isHtmlFileRecord,
    canEdit,
    canvas,
    data?.loading,
  ]);

  const handleDelete = () => {
    revokeLocalPreview(id);
    canvas.removeNode(id);
  };

  const handleChatSubmit = async (message: string) => {
    if (!data?.entity) return;

    const flowSessionId = await dispatchProcess({
      nodeId: id,
      entity: layoutEntity ?? data.entity,
      action: "chat",
      customPrompt: message,
    });

    setShowChatInput(false);

    if (flowSessionId && canvasId) {
      openCanvasFloatingSession(flowSessionId);
    }
  };

  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  // Single-select: hover, chat input, or selection. Hidden during drag and resize.
  // An editor sees a locked node's Unlock toolbar instead.
  const showToolbar =
    !(locked && canvas.canEdit) &&
    !dragging &&
    !isResizing &&
    !isMultiSelectToolbarOpen &&
    (isToolbarHovered ||
      showChatInput ||
      (selected && selectedNodeCount === 1));

  // Stable across zoom steps and hover flips: it is the memoised card's
  // onDoubleClick, and a fresh identity would re-render the card subtree.
  const handleEntityClick = useCallback(async () => {
    if (!layoutEntity) return;

    const entity = layoutEntity;
    const entityData = getEntityData(entity);
    if (!entityData?.id) return;

    const targetPane = "rightPane";

    // A canvas card is a link: open the linked canvas as a workspace tab —
    // never the detail pane, never a browser tab. A gone canvas (the card
    // reads Unavailable) has nowhere to open; the gesture is inert.
    if (isCanvasLinkEntity(entity)) {
      if (canvasLinkHydration?.isGone) return;
      openCanvasLink(entity);
      return;
    }

    if ("type" in entity && (entity as any).type === "skill") {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.SKILLS.SHOW, {
          id: entityData.id.toString(),
        }),
      });
      return;
    }

    if ("base" in entity && entity.base === "file_record") {
      // Blocks open where documents open — the right pane. The lightbox is
      // still theirs, through Expand and Enter. useOpenFileRecord handles the
      // two-hop navigation pattern for standalone sessions (irrelevant on
      // canvas but safe).
      openFileRecord(String(entityData.id));
      return;
    }

    const isNotionDocument =
      ("type" in entity && (entity as any).type === "notion_mcp_document") ||
      ("base" in entity &&
        entity.base === "document" &&
        entity.platform === "notion");

    if (isNotionDocument) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL, {
          pageId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      (entity.base === "email" || entity.base === "email_thread") &&
      entity.platform === "gmail"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL, {
          emailId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      (entity.base === "issue" ||
        ("type" in entity && entity.type === "github_project_item"))
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.ISSUE_DETAIL, {
          issueId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "pull_request"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.PR_DETAIL, {
          prId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "repo"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.REPO_DETAIL, {
          repoId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "project"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.PROJECT_DETAIL, {
          projectId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "google-drive" &&
      entity.base === "file"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GOOGLE_DRIVE.FILE_DETAIL, {
          fileId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    // Generic entities carry their real platform in data.platform_name — route
    // to that platform's dedicated pane. Unmapped platforms fall through to the
    // generic entity detail pane below.
    if ("base" in entity && entity.base === "generic") {
      const target = resolveGenericEntityPaneTarget(entity);
      if (target) {
        appNavigate({
          pane: targetPane,
          to: target.to,
          options: { state: target.state },
        });
        return;
      }
    }

    appNavigate({
      pane: targetPane,
      to: route(ROUTES.ENTITIES.DETAIL, {
        platform: entity.platform || "cohesive",
        entityId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
  }, [
    layoutEntity,
    appNavigate,
    openFileRecord,
    openCanvasLink,
    canvasLinkHydration?.isGone,
  ]);

  const {
    handlers: entityNodeHandlers,
    hasWebLink,
    hasDetailView,
  } = useCanvasEntityNavigationHandlers(
    layoutEntity,
    canEdit,
    handleDelete,
    () => void handleEntityClick(),
  );

  // A block resolves a document's actions plus Expand. The routing above
  // already parsed the node's display copy (this equals
  // isCanvasIntrinsicRecord({ nodeData })), and Expand needs THIS node's id,
  // so its handler lives here rather than in the record layer.
  const isCanvasIntrinsic = htmlBlock !== null;
  const nodeHandlers = useMemo(
    (): ObjectActionHandlers =>
      isCanvasIntrinsic
        ? { ...entityNodeHandlers, expand: () => canvas.expandNode(id) }
        : entityNodeHandlers,
    [canvas, entityNodeHandlers, id, isCanvasIntrinsic],
  );

  // Canvas entities carry an id-only stub; prefer the cached API record so
  // toolbar file actions (share/publish/download) have `path` etc., matching
  // how the node context menu resolves it.
  const { data: canvasFileRecords = [] } = useCanvasFileRecords(canvasId);

  // Memoised: the card is React.memo'd, so its style prop must keep its
  // identity across the node's own re-renders (zoom steps, hover).
  const cardStyle = useMemo<React.CSSProperties>(
    () => ({
      width: nodeWidth !== DEFAULT_WIDTH ? nodeWidth : undefined,
      // Square cards (image/video/file) size themselves via aspect-square;
      // HTML file records get an explicit height to break the lock.
      ...(nodeHeight !== undefined && { height: nodeHeight }),
    }),
    [nodeWidth, nodeHeight],
  );

  const fileRecordForExport = useMemo<FileRecord | null>(() => {
    if (
      !layoutEntity ||
      !("base" in layoutEntity) ||
      layoutEntity.base !== "file_record"
    ) {
      return null;
    }
    const stub = getEntityData(layoutEntity) as FileRecord;
    return resolveFileRecordFromList(stub, canvasFileRecords) ?? stub;
  }, [layoutEntity, canvasFileRecords]);

  const entityNodeKind = useMemo(
    () =>
      layoutEntity && "base" in layoutEntity
        ? buildCanvasNodeKind(
            "entity",
            layoutEntity.base,
            fileRecordForExport?.kind === "link",
          )
        : layoutEntity && isCanvasLinkEntity(layoutEntity)
          ? buildCanvasNodeKind("entity", "canvas")
          : null,
    [layoutEntity, fileRecordForExport],
  );

  // Shared canvas file-record handler deps (same set the node context menu
  // uses) so the toolbar's More menu gets the 1:1 lifecycle actions: Delete =
  // remove node + trash record; Hide on canvas = non-destructive node.hidden.
  const canvasFileRecordHandlerDeps = useCanvasFileRecordMenuHandlerDeps(
    canvasId,
    fileRecordForExport,
  );

  const { actions: entityUtilityRegistryActions } =
    useCanvasEntityObjectActions({
      kind: entityNodeKind,
      surface: "floating-toolbar",
      enabled: showToolbar && !!entityNodeKind,
      nodeHandlers,
      fileRecord: fileRecordForExport,
      fileRecordHandlerDeps: canvasFileRecordHandlerDeps ?? {
        onOpen: (record) => openFileRecord(record.id),
      },
      state: {
        canEdit,
        hasWebLink,
        hasDetailView,
        isCanvasIntrinsic,
      },
    });

  const utilityActions = useMemo(() => {
    // Temporary simplification: file entity node toolbars show only these
    // utilities (chat + delete render separately; everything else stays
    // available via the three-dot "More" menu). Order matches this list.
    const registryActions = isFileRecordEntity
      ? FILE_ENTITY_TOOLBAR_UTILITY_ORDER.flatMap((id) =>
          entityUtilityRegistryActions.filter((a) => a.id === id),
        )
      : entityUtilityRegistryActions;

    const actions = resolvedActionsToContextMenuActions(
      registryActions,
      ["toolbar-utility"],
      {
        kind: entityNodeKind ?? { type: "canvas-node", nodeType: "entity" },
      },
    );

    return actions;
  }, [entityNodeKind, entityUtilityRegistryActions, isFileRecordEntity]);

  const openNodeContextMenu = useOpenNodeContextMenu();
  const handleMoreClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      openNodeContextMenu(id, e.currentTarget);
    },
    [id, openNodeContextMenu],
  );

  if (!data) {
    return (
      <div className="bg-card border-2 rounded-lg p-4 w-[320px] flex items-center justify-center h-24">
        <div className="text-sm text-muted-foreground">Invalid Entity Node</div>
      </div>
    );
  }

  // ── Skeleton ────────────────────────────────────────────────────────────────
  // Agent placement and collaborators see the shimmer until server media
  // lands. The dropping user's image/video uploads use client-only blob URLs
  // (CanvasLocalFilePreviewContext) so the real card renders immediately.
  const fileRecordData = isFileRecordEntity
    ? (layoutEntity?.data as
        | {
            status?: string;
            url?: string;
            content_type?: string;
            kind?: string;
            preview_urls?: Record<string, string> | null;
          }
        | undefined)
    : null;

  // A file the viewer can no longer read: archived, or never theirs to see.
  // Render-time only — nothing is written back to the document, so restoring
  // the file from the Archive heals the node with no residue to clean up.
  const showUnavailable =
    isFileRecordEntity &&
    fileRecordIsUnavailable({
      hydrationGone: fileRecordHydrationGone,
      hydrationSettled: fileRecordHydrationSettled,
      fileRecordData,
    });

  const showSkeleton =
    !showUnavailable &&
    shouldShowFileRecordSkeleton({
      loading: data.loading,
      isFileRecordEntity,
      fileRecordData,
      localPreview,
      videoHandoffReady,
    });

  // Connect handles render only on the real card (below) — there's nothing
  // useful to wire an edge to on a loading skeleton or an errored node. Since
  // those handles therefore *appear* only when the node swaps from skeleton /
  // error into the card, React Flow needs to re-measure the node so edges (and
  // the live connection-line preview) attach to the freshly-mounted handles;
  // its cached handle bounds from the prior branch are stale/empty otherwise.
  // A node that mounts directly as the card already has its handles measured
  // normally, so we nudge RF only on the transition *into* the card, never on
  // initial mount. This also keeps the screenshot/capture path (where nodes
  // mount straight as cards) free of extra re-measure churn.
  const updateNodeInternals = useUpdateNodeInternals();
  const isCard = !data.error && !showSkeleton && !showUnavailable;
  const wasCardRef = useRef(isCard);
  useEffect(() => {
    if (isCard && !wasCardRef.current) {
      updateNodeInternals(id);
    }
    wasCardRef.current = isCard;
  }, [id, isCard, updateNodeInternals]);

  // ── Error state ────────────────────────────────────────────────────────────
  // URL ingestion failed: keep the node so the user can see the error message
  // and open / copy the original URL before deciding whether to delete.
  if (data.error) {
    const errW = width || DEFAULT_WIDTH;
    const errH = height || DEFAULT_FILE_NODE_HEIGHT;
    return (
      <div className="relative" style={{ width: errW, height: errH }}>
        {/* No connect handles on the error card — an errored node isn't a
            meaningful edge endpoint. */}
        {showUnlockToolbar && (
          <CanvasUnlockToolbar
            nodeIds={[id]}
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={errW}
            topOffset={64}
          />
        )}
        {showToolbar && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={errW}
            topOffset={64}
            onPointerDown={() => setIsToolbarHovered(true)}
          >
            <div
              onMouseEnter={() => setIsToolbarHovered(true)}
              onMouseLeave={() => setIsToolbarHovered(false)}
            >
              <BaseEntityCardFloatingToolbar
                visible={true}
                entityActions={[]}
                utilityActions={
                  data.error.sourceUrl
                    ? (() => {
                        const definition =
                          getObjectActionDefinition("open-in-new-tab");
                        const labelCtx = {
                          kind: {
                            type: "canvas-node" as const,
                            nodeType: "entity" as const,
                          },
                        };
                        return [
                          {
                            key: "open-in-new-tab",
                            label: getActionLabel(definition, labelCtx),
                            icon: createElement(
                              getActionIcon(definition, labelCtx),
                              {
                                className: "h-[17px] w-[17px]",
                              },
                            ),
                            onClick: () =>
                              openCanvasEntityTabUrl(data.error!.sourceUrl!),
                          },
                        ];
                      })()
                    : []
                }
                onChatClick={canEdit ? () => {} : undefined}
                onMoreClick={canEdit ? handleMoreClick : undefined}
              />
            </div>
          </CanvasFloatingToolbarPortal>
        )}

        <div
          className={cn(
            "w-full h-full rounded-xl overflow-hidden flex flex-col bg-neutral-900 border border-neutral-700/50 shadow-md",
            selected && "ring-2 ring-canvas-selection/60",
          )}
          onMouseEnter={() => setIsToolbarHovered(true)}
          onMouseLeave={() => setIsToolbarHovered(false)}
        >
          <div className="flex-1 p-4 flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-lc-warning" />
              <span className="text-sm font-semibold leading-tight text-lc-warning">
                {data.error.title}
              </span>
            </div>
            {data.error.message && (
              <p className="text-xs leading-relaxed text-neutral-400">
                {data.error.message}
              </p>
            )}
          </div>

          {data.error.sourceUrl && (
            <button
              className="flex items-center gap-2 border-t border-neutral-700/50 px-4 py-3 text-left text-neutral-500 transition-colors hover:text-neutral-300"
              onClick={() =>
                window.open(
                  data.error!.sourceUrl,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate text-xs">{data.error.sourceUrl}</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Unavailable ────────────────────────────────────────────────────────────
  // The referenced file is archived, or the viewer cannot read it. Both render
  // this, deliberately: telling them apart would report whether a file you
  // cannot see exists. Like the error card, no connect handles — there is
  // nothing on the far side to wire an edge to.
  if (showUnavailable) {
    return (
      <div
        className="relative"
        style={{
          width: width || DEFAULT_WIDTH,
          height: height || DEFAULT_FILE_NODE_HEIGHT,
        }}
      >
        <FileRecordCardUnavailable
          width={width || DEFAULT_WIDTH}
          height={height || DEFAULT_FILE_NODE_HEIGHT}
          label={
            (layoutEntity?.data as { title?: string } | undefined)?.title ||
            undefined
          }
          className={cn(
            !selected && "hover:ring-1 hover:ring-gold/30",
            selected && "ring-2 ring-canvas-selection/60",
          )}
        />
      </div>
    );
  }

  if (showSkeleton) {
    const skeletonW = width || DEFAULT_WIDTH;
    const skeletonH = height || undefined;
    const entityType =
      (data.entity as { type?: string } | undefined)?.type ?? "";
    const contentType = fileRecordData?.content_type ?? "";
    const skeletonKind: FileRecordSkeletonKind =
      contentType.startsWith("image/") || entityType === "googleai_image"
        ? "image"
        : contentType.startsWith("video/") || entityType === "googleai_video"
          ? "video"
          : "file_record";
    const skeletonLabel =
      (layoutEntity?.data as { title?: string } | undefined)?.title ||
      undefined;

    return (
      <div className="relative" style={{ width: skeletonW, height: skeletonH }}>
        {/* No connect handles on the loading skeleton — they appear with the
            real card once media lands (see the updateNodeInternals nudge above). */}
        <FileRecordCardSkeleton
          width={skeletonW}
          height={skeletonH}
          kind={skeletonKind}
          label={skeletonLabel}
          // Same select/hover affordances as the real card so it's obvious the
          // skeleton is a normal node — selectable and deletable (useful for
          // manually cleaning up a placeholder whose tool run was lost).
          className={cn(
            !selected && "hover:ring-1 hover:ring-gold/30",
            selected && "ring-2 ring-canvas-selection/60",
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative w-[320px]",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width: nodeWidth !== DEFAULT_WIDTH ? nodeWidth : undefined }}
    >
      {/* React Flow edge connection handles — one grabbable source per side
          (loose connection mode lets each act as source or target). */}
      <CanvasConnectHandles />

      {/* NodeResizer — only shown when the node is selected. Outer condition
          gates the render (same pattern as ShapeNode) so the handles never
          exist in the DOM for unselected nodes. */}
      {isResizable && canEdit && !!selected && !dragging && (
        <NodeResizer
          keepAspectRatio={keepResizeAspect}
          minWidth={resizerMinW}
          minHeight={resizerMinH}
          // While the user is inside a block the resizer frame IS the one
          // border: it turns blue (focus-for-interaction) instead of the
          // card stacking a second ring on top of the rust selection frame.
          lineClassName={
            htmlBlock?.focusMode != null ? "canvas-resizer-live" : undefined
          }
          handleClassName={
            htmlBlock?.focusMode != null ? "canvas-resizer-live" : undefined
          }
          onResizeStart={() => {
            setIsResizing(true);
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            setIsResizing(false);
            isResizingRef.current = false;
            // Settle dimensions on the lattice. Media keeps its aspect:
            // width snaps, height follows the ratio (exception register X1).
            const w = Math.max(resizerMinW, snapToGrid(params.width, LATTICE));
            const h = keepResizeAspect
              ? Math.round(w * (params.height / params.width))
              : Math.max(resizerMinH, snapToGrid(params.height, LATTICE));
            canvas.updateNodeDimensions(id, { width: w, height: h });
          }}
        />
      )}

      {showUnlockToolbar && (
        <CanvasUnlockToolbar
          nodeIds={[id]}
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={nodeWidth}
          topOffset={64}
        />
      )}

      {/* Floating toolbar — uses CanvasFloatingToolbarPortal so useViewport()
          is only subscribed when the toolbar is actually visible. Unselected
          nodes pay zero viewport-tracking cost. */}
      {showToolbar && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={nodeWidth}
          topOffset={64}
          onPointerDown={() => setIsToolbarHovered(true)}
        >
          <div
            onMouseEnter={() => setIsToolbarHovered(true)}
            onMouseLeave={() => setIsToolbarHovered(false)}
          >
            {showChatInput ? (
              <BaseEntityCardQuickChat
                visible={true}
                onSubmit={handleChatSubmit}
                onClose={() => setShowChatInput(false)}
              />
            ) : (
              <BaseEntityCardFloatingToolbar
                visible={true}
                entityActions={entityActions}
                utilityActions={utilityActions}
                onChatClick={canEdit ? () => setShowChatInput(true) : undefined}
                onMoreClick={canEdit ? handleMoreClick : undefined}
              />
            )}
          </div>
        </CanvasFloatingToolbarPortal>
      )}

      {/* Entity card */}
      {data.entity ? (
        <div
          className={cn("relative", htmlBlock && "group/block")}
          data-block-expanded={isExpanded ? "" : undefined}
          data-entity-type={
            "type" in data.entity ? String(data.entity.type) : undefined
          }
          data-entity-base={
            "base" in data.entity ? String(data.entity.base) : undefined
          }
        >
          <CanvasEntityCard
            entity={
              (fileRecordCardEntity ?? layoutEntity ?? data.entity) as Entity
            }
            isReference={fileRecordForExport?.kind === "link"}
            canvasLink={canvasLinkHydration}
            onCanvasLinkOpen={
              canvasLinkHydration && !canvasLinkHydration.isGone
                ? () => void handleEntityClick()
                : undefined
            }
            onDoubleClick={
              // Double-click means "open the object" — for blocks too: a
              // single click already operates the block (the click-through
              // doorway), so the pair is free to open the detail view.
              handleEntityClick
            }
            toolbarMode="external"
            selected={selected}
            focused={htmlBlock ? htmlBlock.focusMode != null : undefined}
            resizing={isResizing}
            display={htmlBlock?.display}
            theme={htmlBlock?.theme}
            transformHtml={htmlBlock?.transformHtml ?? presign.transformHtml}
            mountPolicy={isPublicCanvasView ? "progressive" : "eager"}
            previewImageUrl={htmlPreviewImageUrl}
            isInteractive={htmlBlock?.iframeOwnsPointer}
            hintLabel={
              htmlBlock && htmlBlock.focusMode === null && !data?.loading
                ? htmlBlock.enterHintLabel
                : null
            }
            islandHint={htmlBlock?.islandHint}
            editFocusPoint={htmlBlock?.editFocusPoint}
            onRuntimeReady={htmlBlock?.onRuntimeReady}
            onBlockMessage={htmlBlock?.onBlockMessage}
            blockClickThrough={blockClickThrough}
            onBlockInteractiveClickThrough={htmlBlock?.enterInteractive}
            islandsEditable={htmlBlock?.islandsEditable}
            contentFrozen={htmlBlock?.contentFrozen}
            className={cn(
              // w-[320px] is the base class; inline style overrides it when resized.
              "rounded-lg relative w-[320px] border",
              !selected && "hover:border-gold/30",
              // Focused-for-interaction (inside the block) reads blue; a mere
              // single-click selection keeps the rust selection ring.
              // Chromeless blocks get NEITHER: the ring is card-edge chrome,
              // and with no card edge it paints a second rectangle against
              // the resizer frame — a double border. Selection reads from
              // the resizer, mode from the chrome foot.
              // Inside a block (focusMode set) the resizer frame carries the
              // blue live state — no card ring at all, so there is only ever
              // one border around a live block.
              selected &&
                htmlBlock?.focusMode == null &&
                !htmlBlock?.display.chromeless &&
                "ring-2 ring-canvas-selection/60",
              isHighlighted &&
                "ring-2 ring-canvas-selection/80 dark:ring-canvas-selection/80",
              data?.entity && "cursor-pointer select-none",
              // Chromeless blocks own their whole box: the node chrome
              // (border, radius, hover ring) would otherwise draw a frame
              // around a document that asked for none.
              htmlBlock?.display.chromeless &&
                "rounded-none border-0 hover:border-transparent",
            )}
            style={cardStyle}
            onVideoDisplayReady={
              isVideoLocalPreview ? handleVideoDisplayReady : undefined
            }
          />
          {blockStatus && (
            // The notes' chrome kit, carrying state in its foot: same type,
            // color, and counter-scaling, in the gutter below the node rather
            // than over block content or stacked under the toolbar. The foot
            // reveals on hover through the wrapper's data-hovered; while the
            // user is inside the block it stays pinned regardless.
            <CanvasObjectChrome
              label=""
              width={nodeWidth}
              zoom={zoom}
              active={htmlBlock?.focusMode != null ? true : "hover"}
              selected={!!selected}
              status={blockStatus}
            />
          )}
          {htmlBlock && lightboxRecord && (
            <AnimatePresence>
              {htmlBlock.lightbox.isOpen && (
                <HtmlBlockLightbox
                  key="lightbox"
                  record={lightboxRecord}
                  htmlBlock={htmlBlock}
                  // The record's authored box, not the node's: the node box is
                  // a per-placement canvas-layout choice (often squeezed), and
                  // the lightbox is the "show it properly" surface — same box
                  // the detail pane uses, so the two full-size surfaces agree.
                  // Records from before display metadata fall back to the node.
                  baseWidth={lightboxRecord.display?.width || nodeWidth}
                  baseHeight={
                    lightboxRecord.display?.height ||
                    (nodeHeight ?? DEFAULT_FILE_NODE_HEIGHT)
                  }
                />
              )}
            </AnimatePresence>
          )}
        </div>
      ) : (
        <div className="bg-card border-2 rounded-lg p-4 w-full flex items-center justify-center h-24">
          <div className="text-sm text-muted-foreground">Empty Entity Node</div>
        </div>
      )}
    </div>
  );
}
